/**
 * Shared pieces for home layouts W, X and Y. Everything here is self-contained: the demos play the
 * real film from /home/film.mp4 by time range (no backend, no share link), so they work logged out,
 * on localhost and in production alike.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import logoMark from "../../../../../assets/brand/logo_site.svg";
import logoMarkLight from "../../../../../assets/brand/logo_site_onlight.svg";
import UploadDownloadArea from "../UploadDownloadArea";
import { FILM_BY_ID, FILM_ROWS, FILM_TITLE, FilmRow, QR_PATH, QR_SIZE, fmtTime, thumbPos } from "./filmData";
import { ICON, IconKey } from "./brandIcons";
import { AI, AISEC, APP_STORE_URL, BADGES, PLACES, PLAY_STORE_URL, SHEET, SOUND, storeLink } from "../../constants/homeLayoutsText";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Instrument+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap";

/** Load the three faces once (Outfit for display, Instrument Sans for reading, JetBrains Mono for timecodes). */
export const useHomeFonts = () => {
  useEffect(() => {
    if (document.querySelector(`link[data-home3-fonts]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONT_HREF;
    l.setAttribute("data-home3-fonts", "1");
    document.head.appendChild(l);
  }, []);
};

export const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
};

export const scrollToId = (id: string) => {
  try {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (e) {
    /* ignore */
  }
};

/** Runs `fn` once, the first time `ref` is at least `threshold` visible. */
export const useOnceVisible = (ref: React.RefObject<Element>, fn: () => void, threshold = 0.4) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (es) => {
        if (es.some((e) => e.isIntersecting)) {
          io.disconnect();
          fnRef.current();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);
};

/* ================================================================== the clip player */

type PlayState = { row: FilmRow; k: number; n: number } | null;

/**
 * Plays [a, b] ranges of one <video>, one after another, like the editor's Play. The progress bar is
 * written straight to the DOM each frame so playback never re-renders the page.
 */
export const useClipPlayer = (videoRef: React.RefObject<HTMLVideoElement>, barRef: React.RefObject<HTMLElement>) => {
  const [state, setState] = useState<PlayState>(null);
  const [finished, setFinished] = useState(0);
  type Api = { play: (list: FilmRow[]) => void; stop: (done?: boolean) => void; toggle: () => boolean; cancel: () => void };
  const api = useRef<Api | null>(null);
  if (!api.current) {
    let queue: FilmRow[] = [];
    let qi = 0;
    let raf = 0;
    let cur: FilmRow | null = null;
    const setBar = (p: number) => {
      if (barRef.current) barRef.current.style.width = `${Math.max(0, Math.min(100, p))}%`;
    };
    const stop = (done = false) => {
      cancelAnimationFrame(raf);
      videoRef.current?.pause();
      cur = null;
      setBar(0);
      setState(null);
      if (done) setFinished((f) => f + 1);
    };
    const tick = () => {
      const v = videoRef.current;
      if (!v || !cur) return;
      setBar(((v.currentTime - cur.a) / (cur.b - cur.a)) * 100);
      if (v.currentTime >= cur.b - 0.04) {
        qi += 1;
        if (qi < queue.length) start(queue[qi]);
        else stop(true);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    const start = (r: FilmRow) => {
      const v = videoRef.current;
      if (!v) return;
      cur = r;
      setState({ row: r, k: qi, n: queue.length });
      const go = () => {
        v.currentTime = r.a;
        const p = v.play();
        if (p && p.catch) p.catch((): void => undefined);
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(tick);
      };
      if (v.readyState >= 1) go();
      else {
        v.addEventListener("loadedmetadata", go, { once: true });
        v.load();
      }
    };
    api.current = {
      play: (list) => {
        queue = list.slice();
        qi = 0;
        if (list.length) start(list[0]);
      },
      stop,
      /** Pause or resume the clip that is playing; false when nothing is. */
      toggle: () => {
        const v = videoRef.current;
        if (!v || !cur) return false;
        if (v.paused) {
          const p = v.play();
          if (p && p.catch) p.catch((): void => undefined);
          raf = requestAnimationFrame(tick);
        } else {
          v.pause();
          cancelAnimationFrame(raf);
        }
        return true;
      },
      cancel: () => cancelAnimationFrame(raf),
    };
  }
  useEffect(() => () => api.current?.cancel(), []);
  const { play, stop, toggle } = api.current;
  return { state, finished, play, stop, toggle };
};

/* ================================================================== small pieces */

/** The cherry mark. `onLight` = the version with a dark ▶ for light grounds. */
export const Mark: React.FC<{ size?: number; className?: string; onLight?: boolean }> = ({ size = 28, className, onLight = false }) => (
  <img className={className} src={onLight ? logoMarkLight : logoMark} alt="" height={size} style={{ width: "auto", display: "block" }} />
);

export const Wordmark: React.FC<{ size?: number; onLight?: boolean }> = ({ size = 28, onLight = false }) => (
  <span className="h3-brand">
    <Mark size={size} onLight={onLight} />
    <span className="h3-wm">
      Cherry<span className="ck">Clipz</span>
    </span>
  </span>
);

export const BrandIcon: React.FC<{ k: IconKey | "cc" | "link"; size?: number }> = ({ k, size = 44 }) => {
  if (k === "cc")
    return (
      <span className="h3-tile cc" style={{ width: size, height: size }}>
        <Mark size={size * 0.62} />
      </span>
    );
  if (k === "link")
    return (
      <span className="h3-tile ln" style={{ width: size, height: size }}>
        <svg viewBox="0 0 24 24" aria-hidden>
          <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
          <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
        </svg>
      </span>
    );
  if (k === "gplay")
    return (
      <span className="h3-tile gplay" style={{ width: size, height: size }}>
        {/* Google's own Play mark, unchanged (public/media/brand/google-play.svg). */}
        <img src="/media/brand/google-play.svg" alt="" style={{ width: "62%", height: "62%", objectFit: "contain" }} />
      </span>
    );
  return (
    <span className={`h3-tile ${k}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 24 24" aria-hidden>
        <path d={ICON[k]} />
      </svg>
    </span>
  );
};

/** Autoplaying, muted, looping clip (our "GIFs"). Pauses off screen; a still for reduced motion. */
export const LoopVideo: React.FC<{ src: string; poster: string; className?: string; label: string }> = ({ src, poster, className, label }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const still = useMemo(prefersReducedMotion, []);
  useEffect(() => {
    const v = ref.current;
    if (!v || still || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((es) =>
      es.forEach((e) => {
        if (e.isIntersecting) {
          const p = v.play();
          if (p && p.catch) p.catch((): void => undefined);
        } else v.pause();
      }),
    );
    io.observe(v);
    return () => io.disconnect();
  }, [still]);
  if (still) return <img className={className} src={poster} alt={label} />;
  return <video ref={ref} className={className} src={src} poster={poster} muted loop playsInline preload="metadata" aria-label={label} />;
};

/** iPhone → App Store, Android → Google Play, anything else → null. */
export const storeForThisPhone = (): string | null => {
  try {
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return PLAY_STORE_URL;
    if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return APP_STORE_URL;
  } catch (e) {
    /* ignore */
  }
  return null;
};

/** The two store badges, plus (on desktop) a QR code that opens the right store on the phone. */
export const StoreBadges: React.FC<{ qr?: boolean; size?: "lg" | "md"; onDark?: boolean; tag?: string }> = ({ qr = false, size = "lg", onDark = false, tag = "home" }) => {
  // On a phone, show only that phone's store (an iPhone has no use for the Play badge).
  const mine = useMemo(storeForThisPhone, []);
  return (
  <div className={`h3-stores ${size}`}>
    <div className="h3-badges">
      {mine !== PLAY_STORE_URL && (
      <a href={storeLink(APP_STORE_URL, tag)} target="_blank" rel="noreferrer" aria-label="Download on the App Store">
        <img className="b-apple" src={onDark ? "/app/appstore-badge-white.svg" : "/app/appstore-badge-black.svg"} alt="Download on the App Store" />
      </a>
      )}
      {mine !== APP_STORE_URL && (
      <a href={storeLink(PLAY_STORE_URL, tag)} target="_blank" rel="noreferrer" aria-label="Get it on Google Play">
        <img className="b-play" src="/app/googleplay-badge.png" alt="Get it on Google Play" />
      </a>
      )}
    </div>
    {qr && (
      <div className="h3-qr">
        <svg viewBox={`-2 -2 ${QR_SIZE + 4} ${QR_SIZE + 4}`} role="img" aria-label="QR code that opens CherryClipz in your phone's app store">
          <rect x="-2" y="-2" width={QR_SIZE + 4} height={QR_SIZE + 4} rx="2.5" fill="#fff" />
          <path d={QR_PATH} stroke="#17110f" fill="none" />
          <rect x={QR_SIZE / 2 - 4.5} y={QR_SIZE / 2 - 4.5} width="9" height="9" rx="2" fill="#fff" />
          <image href={logoMarkLight} x={QR_SIZE / 2 - 3.6} y={QR_SIZE / 2 - 3.8} width="7.2" height="7.6" />
        </svg>
        <span>
          <b>{BADGES.scanTitle}</b>
          {BADGES.scanNote}
        </span>
      </div>
    )}
  </div>
  );
};

/** A phone frame playing a video muted on loop; tap for sound (restarts from the top). */
/** `eager` only for a phone that is on screen at load (the hero); the rest wait until scrolled to. */
export const PhoneVideo: React.FC<{ src: string; poster: string; glow?: string; label: string; eager?: boolean }> = ({ src, poster, glow = "#e61d50", label, eager = false }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [sound, setSound] = useState(false);
  const still = useMemo(prefersReducedMotion, []);
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((es) =>
      es.forEach((e) => {
        if (e.isIntersecting && (!still || sound)) {
          const p = v.play();
          if (p && p.catch) p.catch((): void => undefined);
        } else if (!e.isIntersecting) v.pause();
      }),
    );
    io.observe(v);
    return () => io.disconnect();
  }, [still, sound]);
  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    if (!sound) {
      v.muted = false;
      v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch((): void => undefined);
      setSound(true);
    } else {
      v.muted = true;
      setSound(false);
    }
  };
  return (
    <div className="h3-phonewrap" style={{ ["--glow" as string]: glow } as React.CSSProperties}>
      <div className="h3-device">
        <video ref={ref} src={src} poster={poster} muted loop playsInline preload={eager ? "metadata" : "none"} aria-label={label} autoPlay={eager && !still} />
        <button type="button" className={`h3-sound ${sound ? "on" : ""}`} onClick={toggleSound}>
          <svg viewBox="0 0 24 24" aria-hidden>
            <path d="M4 9v6h4l5 4V5L8 9H4z" />
            {sound ? <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" fill="none" strokeWidth="2" strokeLinecap="round" /> : <path d="M16 9l5 6M21 9l-5 6" fill="none" strokeWidth="2" strokeLinecap="round" />}
          </svg>
          {sound ? SOUND.off : SOUND.on}
        </button>
      </div>
    </div>
  );
};

/** A phone frame playing a real screen recording of the app (muted loop, only while on screen). */
export const PhoneClip: React.FC<{ src: string; label: string; className?: string }> = ({ src, label, className }) => (
  <div className={`h3-device shot ${className || ""}`}>
    <LoopVideo src={`${src}.mp4`} poster={`${src}.jpg`} label={label} />
  </div>
);

export const PhoneShot: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className }) => (
  <div className={`h3-device shot ${className || ""}`}>
    <img src={src} alt={alt} loading="lazy" />
  </div>
);

/** The real uploader, framed dark: it is built for the dark app and its text is white. */
export const UploadCard: React.FC = () => (
  <div className="h3-upload" id="h3-upload">
    <UploadDownloadArea />
  </div>
);

/* ================================================================== the interactive sheet */

const MOOD_SYM: Record<FilmRow["mood"], string> = { p: "+", n: "=", g: "−" };

/**
 * The editor in miniature, on a real film: search, speaker + mood filters, sort, tick to pick
 * (numbered in pick order), click to play, Play picks plays them back to back.
 * `autoDemo` types "young" and ticks three lines the first time it scrolls into view.
 */
export const ClipSheet: React.FC<{ autoDemo?: boolean; height?: number }> = ({ autoDemo = true, height = 468 }) => {
  const [q, setQ] = useState("");
  const [spk, setSpk] = useState<string[]>([]);
  const [mood, setMood] = useState<FilmRow["mood"][]>([]);
  const [sort, setSort] = useState<{ by: "time" | "len"; dir: 1 | -1 }>({ by: "time", dir: 1 });
  const [picks, setPicks] = useState<number[]>([]);
  const [flash, setFlash] = useState<number | null>(null);
  const [nudge, setNudge] = useState(false);
  const vRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLElement>(null);
  const winRef = useRef<HTMLDivElement>(null);
  const qRef = useRef<HTMLInputElement>(null);
  const touched = useRef(false);
  const timers = useRef<number[]>([]);
  const player = useClipPlayer(vRef, barRef);

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = FILM_ROWS.filter((r) => (!needle || r.t.toLowerCase().includes(needle)) && (!spk.length || spk.includes(r.spk)) && (!mood.length || mood.includes(r.mood)));
    const key = sort.by === "len" ? (r: FilmRow) => r.d : (r: FilmRow) => r.a;
    return out.sort((x, y) => (key(x) - key(y)) * sort.dir);
  }, [q, spk, mood, sort]);

  const hi = (t: string) => {
    const n = q.trim();
    if (!n) return t;
    const parts = t.split(new RegExp(`(${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
    return parts.map((p, k) => (k % 2 ? <mark key={k}>{p}</mark> : <React.Fragment key={k}>{p}</React.Fragment>));
  };
  const togglePick = (i: number) => setPicks((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
  const total = picks.reduce((s, i) => s + FILM_BY_ID[i].d, 0);
  const playing = player.state?.row.i;

  const user = () => {
    if (touched.current) return;
    touched.current = true;
    timers.current.forEach((t) => window.clearTimeout(t));
  };

  useOnceVisible(winRef, () => {
    if (!autoDemo || touched.current || prefersReducedMotion()) return;
    const later = (fn: () => void, ms: number) => timers.current.push(window.setTimeout(() => !touched.current && fn(), ms));
    let t = 900;
    const word = "young";
    Array.from(word).forEach((_, k) => later(() => setQ(word.slice(0, k + 1)), (t += 140)));
    [14, 17, 18].forEach((i) =>
      later(() => {
        setPicks((p) => (p.includes(i) ? p : [...p, i]));
        setFlash(i);
      }, (t += 650)),
    );
    later(() => setNudge(true), t + 400);
  }, 0.45);
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  const onRowPlay = (r: FilmRow) => {
    if (playing === r.i && player.toggle()) return;
    player.play([r]);
  };

  return (
    <div className="h3-win" ref={winRef} onPointerDownCapture={user} onKeyDownCapture={user}>
      <div className="h3-chrome">
        <div className="dots">
          <i />
          <i />
          <i />
        </div>
        <div className="url">{FILM_TITLE} · {FILM_ROWS.length} clips</div>
        <div className="spacer" />
      </div>
      <div className="h3-app">
        <div className="h3-sheet">
          <div className="tb">
            <div className="tb-top">
              <label className="search" htmlFor="h3-q">
                <svg className="mag" viewBox="0 0 16 16" fill="none" strokeWidth="1.8">
                  <circle cx="7" cy="7" r="5" />
                  <path d="M11 11l3.5 3.5" strokeLinecap="round" />
                </svg>
                <input ref={qRef} id="h3-q" type="search" autoComplete="off" placeholder={SHEET.search} aria-label="Search the transcript" value={q} onChange={(e) => setQ(e.target.value)} />
                {q && (
                  <button className="x" type="button" aria-label="Clear search" onClick={() => setQ("")}>
                    ×
                  </button>
                )}
              </label>
              <span className="count">{list.length === FILM_ROWS.length ? <><b>{FILM_ROWS.length}</b> clips</> : <><b>{list.length}</b> of {FILM_ROWS.length}</>}</span>
            </div>
            <div className="chips">
              <span className="lbl">{SHEET.speaker}</span>
              {["A", "B", "C", "D", "E", "F"].map((s) => (
                <button key={s} type="button" className="chip sp" aria-pressed={spk.includes(s)} onClick={() => setSpk((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x, s]))}>
                  {s}
                </button>
              ))}
              <span className="sep" />
              <span className="lbl">{SHEET.mood}</span>
              {(["p", "n", "g"] as FilmRow["mood"][]).map((m) => (
                <button key={m} type="button" className="chip" aria-pressed={mood.includes(m)} onClick={() => setMood((x) => (x.includes(m) ? x.filter((y) => y !== m) : [...x, m]))}>
                  <span className="sym">{MOOD_SYM[m]}</span>
                  {SHEET.moods[m]}
                </button>
              ))}
              <span className="sep" />
              <span className="lbl">{SHEET.sort}</span>
              {(["time", "len"] as const).map((by) => (
                <button key={by} type="button" className="chip" aria-pressed={sort.by === by} onClick={() => setSort((s) => (s.by === by ? { by, dir: s.dir === 1 ? -1 : 1 } : { by, dir: 1 }))}>
                  {by === "time" ? SHEET.time : SHEET.length}
                  {sort.by === by ? (sort.dir === 1 ? " ▴" : " ▾") : ""}
                </button>
              ))}
            </div>
          </div>
          <div className="thead" aria-hidden>
            {SHEET.cols.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
          <div className="rows" role="list" aria-label="Clips" style={{ height }}>
            {list.length ? (
              list.map((r) => {
                const k = picks.indexOf(r.i);
                return (
                  <div key={r.i} role="listitem" className={`row${k >= 0 ? " on" : ""}${playing === r.i ? " playing" : ""}${flash === r.i ? " flash" : ""}`}>
                    <button className="tick" type="button" aria-pressed={k >= 0} aria-label={k >= 0 ? "Remove pick" : "Pick this clip"} onClick={() => togglePick(r.i)}>
                      <span className="box">{k >= 0 ? k + 1 : ""}</span>
                    </button>
                    <button className="th" type="button" style={{ backgroundPosition: thumbPos(r.i) }} aria-label={`Play clip ${r.i + 1}`} onClick={() => onRowPlay(r)} />
                    <span className={`spk s-${r.spk}`}>{r.spk}</span>
                    <span className={`mood m-${r.mood}`} title={SHEET.moods[r.mood]}>
                      {MOOD_SYM[r.mood]}
                    </span>
                    <div className="txw">
                      <p className="tx" onClick={() => onRowPlay(r)}>
                        {hi(r.t)}
                      </p>
                      <div className="meta-m">
                        <span className={`spk s-${r.spk}`}>{r.spk}</span>
                        <span>
                          {fmtTime(r.a)}–{fmtTime(r.b)}
                        </span>
                        <span>{r.d.toFixed(1)}s</span>
                      </div>
                    </div>
                    <span className="sec">{r.d.toFixed(1)}</span>
                    <span className="tc">
                      {fmtTime(r.a)} · {fmtTime(r.b)}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="empty-rows">{SHEET.noMatch(q)}</p>
            )}
          </div>
        </div>
        <div className="h3-player">
          <div className="screen">
            <video ref={vRef} src="/home/film.mp4" poster="/home/poster.jpg" preload="metadata" playsInline />
            {!player.state && (
              <button
                className="bigplay"
                type="button"
                aria-label="Play"
                onClick={() => {
                  user();
                  player.play(picks.length ? picks.map((i) => FILM_BY_ID[i]) : [list[0] || FILM_ROWS[2]]);
                }}
              >
                <svg viewBox="0 0 10 12">
                  <path d="M0 0v12l10-6z" />
                </svg>
              </button>
            )}
            <div className={`cap${player.state ? "" : " idle"}`}>{player.state ? player.state.row.t : SHEET.idle}</div>
            <div className="pbar">
              <i ref={barRef as React.RefObject<HTMLElement>} />
            </div>
          </div>
          <div className="now">
            <span>{player.state ? (player.state.n > 1 ? <b>Clip {player.state.k + 1} of {player.state.n}</b> : <b>Speaker {player.state.row.spk}</b>) : player.finished ? SHEET.done : SHEET.nothing}</span>
            <span>{player.state ? `${fmtTime(player.state.row.a)} · ${fmtTime(player.state.row.b)}` : ""}</span>
          </div>
          <div className="picks">
            <div className="ph">
              <strong>{SHEET.yourVideo}</strong>
              <span className="pc">
                {picks.length} clip{picks.length === 1 ? "" : "s"} · {fmtTime(total)}
              </span>
              <button className="clear" type="button" onClick={() => setPicks([])}>
                {SHEET.clear}
              </button>
            </div>
            <ol className="strip">
              {picks.map((i, k) => (
                <li key={i}>
                  <button type="button" className={playing === i && (player.state?.n || 0) > 1 ? "cur" : ""} style={{ backgroundPosition: thumbPos(i) }} aria-label={`Play pick ${k + 1}`} onClick={() => player.play([FILM_BY_ID[i]])}>
                    <span className="n">{k + 1}</span>
                  </button>
                </li>
              ))}
            </ol>
            {!picks.length && <p className="strip-empty">{SHEET.emptyPicks}</p>}
          </div>
          <div className="pact">
            <button
              className={`h3-btn cherry${nudge ? " nudge" : ""}`}
              type="button"
              disabled={!picks.length}
              onClick={() => {
                setNudge(false);
                player.play(picks.map((i) => FILM_BY_ID[i]));
              }}
            >
              <span className="tri" />
              {picks.length > 1 ? `Play ${picks.length} · ${fmtTime(total)}` : SHEET.playPicks}
            </button>
            <button className="h3-btn ghost" type="button" onClick={() => scrollToId("h3-upload")}>
              {SHEET.export}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== the AI demo */

type Assistant = "claude" | "openai";
const ASSISTANT_NAME: Record<Assistant, string> = { claude: "Claude", openai: "ChatGPT" };

/** Map typing onto one of the four ready-made asks, or -1 when it matches none (this is a demo). */
const matchPrompt = (text: string): number => {
  const s = text.toLowerCase();
  let best = -1;
  let score = 0;
  AI.prompts.forEach((p, k) => {
    const hit = p.keys.filter((key) => s.includes(key)).length;
    if (hit > score) {
      score = hit;
      best = k;
    }
  });
  return best;
};

/**
 * "Your AI picks, you stay in charge": a chat that shows its steps, ticks real clips into a sheet
 * panel, then shows YOU swapping one, and plays the cut. Only knows the four chips; anything else
 * typed gets an honest "this demo only knows four asks". `layout="hero"` = the big prompt box (X).
 */
export const AiCutDemo: React.FC<{ layout?: "hero" | "split"; autoRun?: boolean; cycleExamples?: boolean }> = ({ layout = "split", autoRun = true, cycleExamples = false }) => {
  const [asst, setAsst] = useState<Assistant>("claude");
  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState("");
  const [active, setActive] = useState<number | null>(null);
  const [ask, setAsk] = useState("");
  const [steps, setSteps] = useState<("" | "run" | "done")[]>(["", "", "", ""]);
  const [picked, setPicked] = useState<number[]>([]);
  const [swapAt, setSwapAt] = useState<number | null>(null);
  const [reply, setReply] = useState("");
  const [unknown, setUnknown] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const vRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLElement>(null);
  const timers = useRef<number[]>([]);
  const run = useRef(0);
  const touched = useRef(false);
  const player = useClipPlayer(vRef, barRef);

  const clear = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  useEffect(() => clear, []);

  const reset = (askText: string) => {
    clear();
    player.stop();
    setAsk(askText);
    setTyping("");
    setDraft("");
    setSteps(["", "", "", ""]);
    setPicked([]);
    setSwapAt(null);
    setReply("");
    setUnknown(false);
  };

  const runPrompt = (p: number, askText?: string) => {
    const P = AI.prompts[p];
    reset(askText || P.ask);
    const id = ++run.current;
    const fast = prefersReducedMotion();
    const later = (fn: () => void, ms: number) => timers.current.push(window.setTimeout(() => run.current === id && fn(), fast ? 0 : ms));
    setActive(p);
    let t = 250;
    [0, 1, 2].forEach((s) => {
      later(() => setSteps((x) => x.map((v, k) => (k === s ? "run" : v)) as typeof x), (t += 60));
      later(() => setSteps((x) => x.map((v, k) => (k === s ? "done" : v)) as typeof x), (t += 600));
    });
    later(() => setSteps((x) => x.map((v, k) => (k === 3 ? "run" : v)) as typeof x), (t += 60));
    P.ids.forEach((i) => later(() => setPicked((x) => [...x, i]), (t += 240)));
    later(() => {
      setSteps(["done", "done", "done", "done"]);
      const len = P.ids.reduce((s, i) => s + FILM_BY_ID[i].d, 0);
      setReply(AI.reply(P.ids.length, fmtTime(len), P.say));
    }, (t += 300));
    // the point of the demo: you can see every pick, and you change one
    later(() => {
      const at = P.ids.indexOf(P.swap[0]);
      setPicked(P.ids.map((i) => (i === P.swap[0] ? P.swap[1] : i)));
      setSwapAt(at);
    }, (t += 1400));
  };

  const runUnknown = (text: string) => {
    reset(text);
    ++run.current;
    setActive(null);
    setUnknown(true);
  };

  useOnceVisible(rootRef, () => {
    if (prefersReducedMotion()) {
      if (autoRun) runPrompt(0);
      return;
    }
    if (cycleExamples) {
      let t = 300;
      const s = AI.prompts[0].ask;
      Array.from(s).forEach((_, j) => timers.current.push(window.setTimeout(() => !touched.current && setTyping(s.slice(0, j + 1)), (t += 28))));
      timers.current.push(window.setTimeout(() => !touched.current && runPrompt(0, s), (t += 450)));
    } else if (autoRun) runPrompt(0);
  }, 0.35);

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    touched.current = true;
    const text = draft.trim();
    if (!text) return runPrompt(active ?? 0);
    const k = matchPrompt(text);
    if (k < 0) runUnknown(text);
    else runPrompt(k, text);
  };
  const total = picked.reduce((s, i) => s + FILM_BY_ID[i].d, 0);
  const done = swapAt !== null;
  const playCut = () => player.play(picked.map((i) => FILM_BY_ID[i]));

  const chips = (
    <div className="h3-prompts">
      {AI.prompts.map((p, k) => (
        <button
          key={p.chip}
          type="button"
          aria-pressed={active === k}
          onClick={() => {
            touched.current = true;
            runPrompt(k);
          }}
        >
          {p.chip}
        </button>
      ))}
    </div>
  );
  const input = (
    <form className="h3-input" onSubmit={submit}>
      <input
        id={`h3-ask-${layout}`}
        value={draft || typing}
        onChange={(e) => {
          touched.current = true;
          clear();
          setTyping("");
          setDraft(e.target.value);
        }}
        onFocus={() => {
          if (!touched.current) {
            touched.current = true;
            clear();
            setTyping("");
          }
        }}
        placeholder={AI.placeholder}
        aria-label="Ask for a cut"
        autoComplete="off"
      />
      <button className="send" type="submit" aria-label="Send">
        <svg viewBox="0 0 16 16">
          <path d="M2 8.5 14 2 9.5 14l-1.8-4.7z" />
        </svg>
      </button>
    </form>
  );
  const assistantToggle = (
    <div className="h3-seg" role="group" aria-label="Choose an assistant">
      {(["claude", "openai"] as Assistant[]).map((a) => (
        <button key={a} type="button" aria-pressed={asst === a} onClick={() => setAsst(a)}>
          <svg viewBox="0 0 24 24" aria-hidden className={a}>
            <path d={ICON[a]} />
          </svg>
          {ASSISTANT_NAME[a]}
        </button>
      ))}
    </div>
  );
  const trace = (
    <div className="h3-chat" aria-live="polite">
      {ask ? (
        <>
          <div className="msg user">{ask}</div>
          <div className="msg bot">
            <BrandIcon k={asst} size={28} />
            <div className="body">
              {unknown ? (
                <p className="reply">{AI.unknown}</p>
              ) : (
                <>
                  <ul className="h3-steps">
                    {AI.steps.map((s, k) => (
                      <li key={s} className={steps[k]}>
                        <i />
                        {steps[k] === "done" ? AI.stepsDone(AI.prompts[active ?? 0].ids.length)[k] : s}
                      </li>
                    ))}
                  </ul>
                  {reply && <p className="reply">{reply}</p>}
                </>
              )}
            </div>
          </div>
          {swapAt !== null && <div className="msg you">{AI.swapped(swapAt + 1)}</div>}
        </>
      ) : (
        <p className="h3-chat-empty">{AI.waiting}</p>
      )}
    </div>
  );
  const screen = (
    <div className="ai-screen">
      <video ref={vRef} src="/home/film.mp4" poster="/home/poster-ai.jpg" preload="none" playsInline />
      {done && !player.state && (
        <button className="bigplay" type="button" aria-label={AI.playCut} onClick={playCut}>
          <svg viewBox="0 0 10 12">
            <path d="M0 0v12l10-6z" />
          </svg>
        </button>
      )}
      {player.state && <div className="ai-cap">{player.state.row.t}</div>}
      <div className="pbar">
        <i ref={barRef as React.RefObject<HTMLElement>} />
      </div>
    </div>
  );
  const sheet = (
    <div className="h3-panel sheetp">
      <div className="panel-h">
        <span className="who">
          <BrandIcon k="cc" size={28} />
          {AI.yourSheet}
        </span>
        <span className="live">
          <i />
          {AI.live}
        </span>
      </div>
      {layout === "split" && screen}
      <ol className="mini">
        {picked.map((i, k) => {
          const r = FILM_BY_ID[i];
          return (
            <li key={`${k}-${i}`} className={`${player.state?.row.i === i ? "cur" : ""}${swapAt === k ? " swapped" : ""}`} onClick={() => player.play([r])}>
              <span className="n">{k + 1}</span>
              <span className="t" style={{ backgroundPosition: thumbPos(i) }} />
              <span className="s">{r.t}</span>
              {swapAt === k ? <span className="youtag">{AI.you}</span> : <span className="d">{r.d.toFixed(1)}s</span>}
            </li>
          );
        })}
        {!picked.length && <li className="mini-empty">{AI.waiting}</li>}
      </ol>
      <div className="mini-foot">
        <span className="tot">{picked.length ? `${picked.length} clips · ${fmtTime(total)}` : `The Other Side · ${FILM_ROWS.length} clips`}</span>
        <button className="h3-btn cherry sm" type="button" disabled={!done} onClick={playCut}>
          <span className="tri" />
          {AI.playCut}
        </button>
      </div>
    </div>
  );

  if (layout === "hero")
    return (
      <div className="h3-ai hero" ref={rootRef}>
        <div className="h3-askbox">
          <div className="askbox-top">{assistantToggle}</div>
          {input}
          {chips}
        </div>
        <div className={`h3-results${ask ? " show" : ""}`}>
          <div className="h3-panel chatp">
            <div className="panel-h">
              <span className="who">
                <BrandIcon k={asst} size={28} />
                {ASSISTANT_NAME[asst]}
              </span>
            </div>
            {trace}
          </div>
          {sheet}
          <div className="h3-tall">{screen}</div>
        </div>
        <p className="h3-ai-note">{AI.note}</p>
      </div>
    );

  return (
    <div className="h3-ai split" ref={rootRef}>
      <div className="h3-panel chatp">
        <div className="panel-h">
          <span className="who">
            <BrandIcon k={asst} size={28} />
            {ASSISTANT_NAME[asst]}
          </span>
          {assistantToggle}
        </div>
        {trace}
        <div className="composer">
          {chips}
          {input}
        </div>
      </div>
      {sheet}
      <p className="h3-ai-note">{AI.note}</p>
    </div>
  );
};

/** The full AI-connector section: the headline, the demo, and how to connect (shared by W and Y). */
export const AiSection: React.FC<{ id: string }> = ({ id }) => (
  <section id={id} className="h3-aisec">
    <div className="wrap">
      <div className="two">
        <div>
          <span className="eyebrow">{AISEC.eyebrow}</span>
          <h2 className="h2">
            {AISEC.title1}
            <br />
            <span className="rw">{AISEC.title2}</span>
          </h2>
        </div>
        <p className="lede">{AISEC.body}</p>
      </div>
      {/* Who is talking to whom, said with the real marks: Claude and ChatGPT, each wired to CherryClipz. */}
      <div className="h3-linked" aria-label="Claude and ChatGPT, connected to CherryClipz">
        <span className="tile">
          <BrandIcon k="claude" size={46} />
          Claude
        </span>
        <span className="wire" aria-hidden />
        <span className="tile cc">
          <BrandIcon k="cc" size={46} />
          CherryClipz
        </span>
        <span className="wire" aria-hidden />
        <span className="tile">
          <BrandIcon k="openai" size={46} />
          ChatGPT
        </span>
      </div>
      <div style={{ marginTop: "clamp(24px,3vw,36px)" }}>
        <AiCutDemo layout="split" />
      </div>
      <div className="h3-connect">
        <div className="who2">
          <BrandIcon k="claude" size={44} />
          <BrandIcon k="openai" size={44} />
          <h3>{AISEC.connectTitle}</h3>
        </div>
        <ol>
          {AISEC.connect.map((c, k) => (
            <li key={c.t}>
              <span className="n">{k + 1}</span>
              <div>
                <b>{c.t}</b>
                <p>{c.b}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="fine">{AISEC.fine}</p>
      </div>
    </div>
  </section>
);

/* ================================================================== the places a sheet goes */

const PLACE_ICONS: Record<string, ("cc" | "link" | "claude" | "openai" | "pr" | "appstore" | "gplay" | "make" | "youtube")[]> = {
  web: ["cc"],
  phone: ["appstore", "gplay"],
  ai: ["claude", "openai"],
  pr: ["pr"],
  auto: ["make", "youtube"],
  link: ["link"],
};

/** The six places one upload shows up, each with its partner marks. */
export const Places: React.FC = () => (
  <div className="h3-places">
    {PLACES.map((p) => (
      <div key={p.key}>
        <span className="icons">
          {PLACE_ICONS[p.key].map((k) => (
            <BrandIcon key={k} k={k} size={42} />
          ))}
        </span>
        <h3>
          {p.title}
          {p.tag && <em>{p.tag}</em>}
        </h3>
        <p>{p.body}</p>
      </div>
    ))}
  </div>
);

/* ================================================================== pricing (shared) */

export const PlanCards: React.FC<{ onPrimary: () => void; onPlans: () => void; plans: { name: string; price: string; lines: string[]; cta: string; pro: boolean }[]; perMonth: string }> = ({ onPrimary, onPlans, plans, perMonth }) => (
  <div className="h3-plans">
    {plans.map((p, k) => (
      <div key={p.name} className={`plan${p.pro ? " pro" : ""}`}>
        <span className="nm">{p.name}</span>
        <div className="pr">
          {p.price}
          <small>{perMonth}</small>
        </div>
        <ul>
          {p.lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        <button type="button" className={`h3-btn ${p.pro ? "cherry" : "ghost"}`} onClick={k === 0 ? onPrimary : onPlans}>
          {p.cta}
        </button>
      </div>
    ))}
  </div>
);
